import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
  LifeBuoyIcon,
  PackageIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  RadarIcon,
  SettingsIcon,
  ShoppingBasketIcon,
} from '@/shared/ui/icons';
import { useSidebar } from '@/shared/ui/layout/SidebarContext';
import { cn } from '@/shared/utils/cn';

const ITEMS = [
  { to: '/parcels', labelKey: 'nav:parcels', icon: PackageIcon, end: true },
  { to: '/packages', labelKey: 'nav:packages', icon: ShoppingBasketIcon, end: false },
  { to: '/track', labelKey: 'nav:track', icon: RadarIcon, end: false },
  { to: '/support', labelKey: 'nav:support', icon: LifeBuoyIcon, end: false },
  { to: '/settings', labelKey: 'nav:settings', icon: SettingsIcon, end: false },
] as const;

export const SideRail = () => {
  const { t } = useTranslation();
  const { collapsed, toggle } = useSidebar();

  return (
    <aside
      aria-label={t('nav:sidebar')}
      className={cn(
        'fixed bottom-0 left-0 top-16 z-20 hidden flex-col border-r border-slate-200 bg-white transition-[width] md:flex',
        collapsed ? 'w-16' : 'w-60 lg:w-64',
      )}
    >
      <nav className="px-3 py-4">
        <ul className="flex flex-col gap-1">
          {ITEMS.map(({ to, labelKey, icon: Icon, end }) => {
            const label = t(labelKey);
            return (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                title={collapsed ? label : undefined}
                aria-label={collapsed ? label : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                    collapsed && 'justify-center',
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-ink-soft hover:bg-slate-100 hover:text-ink',
                  )
                }
              >
                <Icon className="h-5 w-5" aria-hidden />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            </li>
            );
          })}
        </ul>
      </nav>
      <div className="mt-auto border-t border-slate-200 px-3 py-3">
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? t('nav:expand') : t('nav:collapse')}
          title={collapsed ? t('nav:expand') : t('nav:collapse')}
          className={cn(
            'flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-ink-soft transition-colors hover:bg-slate-100 hover:text-ink',
            collapsed && 'justify-center',
          )}
        >
          {collapsed ? (
            <PanelLeftOpenIcon className="h-5 w-5" aria-hidden />
          ) : (
            <>
              <PanelLeftCloseIcon className="h-5 w-5" aria-hidden />
              <span>{t('nav:collapse')}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
};
